import { supabase } from '../../config/supabase';
import { Database } from '@travel-dashboard/shared';

export type Trip = Database['travel']['Tables']['trips']['Row'] & {
  is_owner?: boolean;
};

export interface CreateTripInput {
  nickname: string;
  description?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  total_budget?: number;
  currency?: string;
}

export interface UpdateTripInput {
  nickname?: string;
  description?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  total_budget?: number;
  currency?: string;
  use_budget?: boolean;
  only_cooperate_card?: boolean;
  round_up_per_diem?: boolean;
}

export const tripsService = {
  /**
   * Get all trips for the current user
   */
  async getUserTrips(userId: string): Promise<Trip[]> {
    const { data, error } = await supabase.rpc('get_user_trips', {
      user_uid: userId,
    });

    if (error) {
      console.error('Error fetching user trips:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get a specific trip by ID
   */
  async getTripById(tripId: string): Promise<Trip | null> {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .eq('id', tripId)
      .single();

    if (error) {
      console.error('Error fetching trip:', error);
      throw error;
    }

    return data;
  },

  /**
   * Get a specific trip by short ID
   */
  async getTripByShortId(shortId: string): Promise<Trip | null> {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .eq('short_id', shortId)
      .single();

    if (error) {
      console.error('Error fetching trip by short_id:', error);
      throw error;
    }

    return data;
  },

  /**
   * Get trip dashboard data with aggregated expenses
   */
  async getTripDashboard(tripId: string) {
    const { data, error } = await supabase.rpc('get_trip_dashboard', {
      trip_guid: tripId,
    });

    if (error) {
      console.error('Error fetching trip dashboard:', error);
      throw error;
    }

    return data?.[0] || null;
  },

  /**
   * Create a new trip
   */
  async createTrip(userId: string, input: CreateTripInput): Promise<Trip> {
    // Generate a random 6-character short ID
    const generateShortId = () => {
      return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    const { data, error } = await supabase
      .from('trips')
      .insert({
        ...input,
        created_by: userId,
        short_id: generateShortId(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating trip:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update an existing trip
   */
  async updateTrip(tripId: string, input: UpdateTripInput): Promise<Trip> {
    const { data, error } = await supabase
      .from('trips')
      .update(input)
      .eq('id', tripId)
      .select()
      .single();

    if (error) {
      console.error('Error updating trip:', error);
      throw error;
    }

    return data;
  },

  /**
   * Delete a trip
   */
  async deleteTrip(tripId: string): Promise<void> {
    const { error } = await supabase
      .from('trips')
      .delete()
      .eq('id', tripId);

    if (error) {
      console.error('Error deleting trip:', error);
      throw error;
    }
  },

  /**
   * Add a participant to a trip
   */
  async addParticipant(tripId: string, userId: string, role: string = 'participant') {
    const { data, error } = await supabase
      .from('trip_participants')
      .insert({
        trip_id: tripId,
        user_id: userId,
        role,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding participant:', error);
      throw error;
    }

    return data;
  },

  /**
   * Remove a participant from a trip
   */
  async removeParticipant(tripId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('trip_participants')
      .delete()
      .eq('trip_id', tripId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  },

  /**
   * Get all participants for a trip
   */
  async getTripParticipants(tripId: string) {
    const { data, error } = await supabase
      .from('trip_participants')
      .select(`
        *,
        users (
          id,
          email,
          display_name,
          photo_url
        )
      `)
      .eq('trip_id', tripId);

    if (error) {
      console.error('Error fetching trip participants:', error);
      throw error;
    }

    return data || [];
  },
};
